import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { BACKEND } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, TrendingUp, Copy, UserMinus, Trophy, BarChart3, Zap, Share2, Sparkles, BadgeCheck } from 'lucide-react';

function FollowDialog({ leader, onFollowed, existing }) {
  const [open, setOpen] = useState(false);
  const [alloc, setAlloc] = useState(existing?.alloc_pct?.toString() || '10');
  const [copyFutures, setCopyFutures] = useState(existing?.copy_futures || false);
  const [maxLeverageCap, setMaxLeverageCap] = useState(existing?.max_leverage_cap || 10);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!open || !leader?.user_id) return;
    setAnalytics(null);
    api.get(`/futures/copy/analytics/${leader.user_id}?window_days=30`)
      .then(r => setAnalytics(r.data))
      .catch(() => {});
  }, [open, leader?.user_id]);

  const runSim = async () => {
    setSimBusy(true);
    try {
      const r = await api.get(`/futures/copy/simulate/${leader.user_id}?alloc_pct=${alloc}&max_leverage_cap=${maxLeverageCap}&lookback=10`);
      setSim(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Simulation failed'); }
    finally { setSimBusy(false); }
  };

  const follow = async () => {
    const pct = parseFloat(alloc);
    if (!pct || pct <= 0 || pct > 100) { toast.error('Allocation must be 1-100%'); return; }
    setBusy(true);
    try {
      await api.post(`/traders/follow/${leader.user_id}`, { alloc_pct: pct });
      // Also push futures copy settings
      await api.post('/futures/copy/settings', {
        leader_id: leader.user_id,
        copy_futures: copyFutures,
        max_leverage_cap: maxLeverageCap,
        alloc_pct: pct,
      }).catch(() => { /* soft-fail if not yet following in some race */ });
      toast.success(`${existing ? 'Updated' : 'Now following'} ${leader.name} — ${pct}% alloc${copyFutures ? ' · Futures ON' : ''}`);
      setOpen(false);
      onFollowed?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className={existing ? 'bg-[#2B3139] hover:bg-[#323842] text-white rounded-sm' : 'bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm'} data-testid={`follow-btn-${leader.user_id}`}>
          {existing ? <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copying · {existing.alloc_pct}%{existing.copy_futures && ' · F'}</> : <>Copy Trade</>}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md">
        <DialogHeader><DialogTitle>Copy {leader.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[#B7BDC6]">
            Every trade this trader makes will be mirrored to your wallet, scaled by your allocation.
          </div>

          {/* 30-day analytics — helps user pick smart before committing */}
          {analytics && analytics.trades > 0 && (
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3 text-[11px]" data-testid="copy-analytics">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-2">Their last 30 days</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <div className="text-[#848E9C]">Trades</div>
                <div className="text-white text-right">{analytics.trades} <span className="text-[#5E6673]">({analytics.wins}W · {analytics.losses}L)</span></div>
                <div className="text-[#848E9C]">Win rate</div>
                <div className={`text-right ${analytics.win_rate >= 50 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{analytics.win_rate}%</div>
                <div className="text-[#848E9C]">Avg leverage</div>
                <div className={`text-right ${analytics.avg_leverage >= 25 ? 'text-[#F6465D]' : 'text-[#B7BDC6]'}`}>{analytics.avg_leverage}×{analytics.avg_leverage >= 25 && ' ⚠'}</div>
                <div className="text-[#848E9C]">Avg PnL / trade</div>
                <div className={`text-right ${analytics.avg_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{analytics.avg_pnl_usdt >= 0 ? '+' : ''}${analytics.avg_pnl_usdt}</div>
                <div className="text-[#848E9C]">Max drawdown</div>
                <div className="text-right text-[#F6465D]">-${analytics.max_drawdown_usdt} <span className="text-[#5E6673]">({analytics.max_drawdown_pct}%)</span></div>
                <div className="text-[#848E9C]">30d total PnL</div>
                <div className={`text-right ${analytics.total_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'} font-semibold`}>{analytics.total_pnl_usdt >= 0 ? '+' : ''}${analytics.total_pnl_usdt}</div>
              </div>
              {Array.isArray(analytics.cum_pnl_series) && analytics.cum_pnl_series.length >= 2 && (
                <div className="mt-2 pt-2 border-t border-[#2B3139]" data-testid="copy-analytics-sparkline">
                  <div className="flex items-center justify-between text-[9px] text-[#5E6673] font-mono uppercase mb-1">
                    <span>30d cumulative PnL</span>
                    <span className={analytics.total_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>
                      {analytics.trades} closes
                    </span>
                  </div>
                  <PnlSparkline series={analytics.cum_pnl_series} />
                </div>
              )}
            </div>
          )}
          {analytics && analytics.trades === 0 && (
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3 text-[11px] text-[#848E9C]" data-testid="copy-analytics-empty">
              No futures trades from this leader in the last 30 days.
            </div>
          )}

          <div>
            <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Allocation per trade (% of wallet)</Label>
            <Input type="number" min="1" max="100" value={alloc} onChange={e=>{setAlloc(e.target.value); setSim(null);}}
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono mt-1.5"
              data-testid="follow-alloc-input" />
          </div>

          {/* Futures copy toggle */}
          <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white text-sm font-semibold flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-[#FCD535]" /> Copy Futures Trades</div>
                <div className="text-[10px] text-[#848E9C]">Mirror perp positions with your own leverage cap</div>
              </div>
              <button
                onClick={() => setCopyFutures(v => !v)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${copyFutures ? 'bg-[#FCD535]' : 'bg-[#2B3139]'}`}
                data-testid="follow-copy-futures-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${copyFutures ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {copyFutures && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[10px] uppercase font-mono text-[#848E9C] mb-1">
                  <span>Max leverage cap</span>
                  <span className="text-[#FCD535]" data-testid="follow-lev-cap-display">{maxLeverageCap}×</span>
                </div>
                <input type="range" min="1" max="50" value={maxLeverageCap} onChange={e=>{setMaxLeverageCap(parseInt(e.target.value)); setSim(null);}} className="w-full accent-[#FCD535]" data-testid="follow-lev-cap-slider" />
                <div className="text-[10px] text-[#5E6673] mt-1">Leader's leverage will be capped at min({maxLeverageCap}×, their setting)</div>
                <Button onClick={runSim} disabled={simBusy} size="sm" variant="outline" className="w-full mt-2 border-[#2B3139] bg-transparent text-white hover:bg-[#2B3139] text-xs rounded-sm" data-testid="follow-run-sim">
                  <Sparkles className="w-3 h-3 mr-1" /> {simBusy ? 'Simulating…' : 'Preview: what if I had copied their last 10 trades?'}
                </Button>
                {sim && (
                  <div className="border border-[#2B3139] bg-[#181A20] p-2 mt-2 text-[11px]" data-testid="follow-sim-result">
                    {sim.count === 0 ? (
                      <div className="text-[#848E9C]">No closed positions to simulate — leader has no history yet.</div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-[#848E9C]">Positions</span>
                          <span className="text-white">{sim.count} ({sim.wins}W · {sim.losses}L)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#848E9C]">Your simulated PnL</span>
                          <span className={sim.total_pnl_usdt >= 0 ? 'text-[#0ECB81] font-semibold' : 'text-[#F6465D] font-semibold'}>
                            {sim.total_pnl_usdt >= 0 ? '+' : ''}${sim.total_pnl_usdt} USDT
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#848E9C]">ROI on margin used</span>
                          <span className={sim.roi_pct >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>
                            {sim.roi_pct >= 0 ? '+' : ''}{sim.roi_pct}%
                          </span>
                        </div>
                        <div className="text-[9px] text-[#5E6673] mt-1">Based on your current wallet (${sim.wallet_usdt_base?.toLocaleString()} USDT) · {sim.alloc_pct}% alloc · {sim.max_leverage_cap}× cap</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button onClick={follow} disabled={busy} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="confirm-follow-btn">
            {busy ? 'Saving…' : existing ? 'Update Copy Settings' : 'Start Copying'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VerifiedHistoryModal({ leader, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !leader?.user_id) return;
    setLoading(true);
    api.get(`/futures/verified_history/${leader.user_id}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, leader?.user_id]);
  const sinceStr = data?.since ? new Date(data.since).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const monthlyMax = data?.months?.length ? Math.max(...data.months.map(m => Math.abs(m.pnl))) : 1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-2xl" data-testid="verified-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-[#1DA1F2] fill-[#1DA1F2]/20" />
            <span>Verified Trader · {leader?.name}</span>
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="py-10 text-center text-[#848E9C] text-sm">Loading proof of history…</div>}
        {!loading && data && data.trades === 0 && (
          <div className="py-10 text-center text-[#848E9C] text-sm">No closed futures history yet.</div>
        )}
        {!loading && data && data.trades > 0 && (
          <div className="space-y-4">
            {/* Header stats */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                <div className="text-[9px] uppercase text-[#5E6673] font-mono">Trading since</div>
                <div className="text-white text-sm font-mono mt-0.5" data-testid="vh-since">{sinceStr}</div>
              </div>
              <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                <div className="text-[9px] uppercase text-[#5E6673] font-mono">Closed trades</div>
                <div className="text-white text-sm font-mono mt-0.5" data-testid="vh-trades">{data.trades}</div>
              </div>
              <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                <div className="text-[9px] uppercase text-[#5E6673] font-mono">Overall win rate</div>
                <div className={`text-sm font-mono mt-0.5 ${data.win_rate >= 50 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="vh-winrate">{data.win_rate}%</div>
              </div>
              <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                <div className="text-[9px] uppercase text-[#5E6673] font-mono">90d PnL</div>
                <div className={`text-sm font-mono mt-0.5 ${data.pnl_90d_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="vh-pnl90d">
                  {data.pnl_90d_usdt >= 0 ? '+' : ''}${data.pnl_90d_usdt}
                </div>
              </div>
            </div>

            {/* Monthly PnL bars */}
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3" data-testid="vh-months">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Monthly PnL · last {data.months.length} months</div>
                <div className={`text-[11px] font-mono ${data.total_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                  All-time {data.total_pnl_usdt >= 0 ? '+' : ''}${data.total_pnl_usdt}
                </div>
              </div>
              <div className="flex items-end gap-1 h-24">
                {data.months.map(m => {
                  const pct = monthlyMax ? (Math.abs(m.pnl) / monthlyMax) * 90 : 0;
                  const up = m.pnl >= 0;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center" data-testid={`vh-month-${m.month}`}>
                      <div className="w-full flex-1 flex flex-col justify-end">
                        <div
                          className={`${up ? 'bg-[#0ECB81]' : 'bg-[#F6465D]'} w-full transition-all`}
                          style={{ height: `${pct}%`, minHeight: m.pnl !== 0 ? '2px' : '0' }}
                          title={`${m.month}: ${up ? '+' : ''}${m.pnl} USDT · ${m.wins}W · ${m.losses}L`}
                        />
                      </div>
                      <div className="text-[8px] font-mono text-[#5E6673] mt-1">{m.month.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Running win-rate curve */}
            {data.win_rate_curve?.length >= 2 && (
              <div className="border border-[#2B3139] bg-[#0B0E11] p-3" data-testid="vh-winrate-curve">
                <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Running win-rate curve</div>
                <WinRateCurve series={data.win_rate_curve} />
              </div>
            )}

            <div className="text-[10px] font-mono text-[#5E6673] text-center pt-1 border-t border-[#2B3139]">
              {data.verified ? '✓ Verified — 90+ days of profitable history' : 'Historical breakdown · not currently verified'}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WinRateCurve({ series }) {
  const W = 560, H = 60, pad = 4;
  const vals = series.map(s => s.wr);
  const minV = 0, maxV = 100;
  const range = maxV - minV;
  const stepX = (W - pad * 2) / Math.max(1, vals.length - 1);
  const y = v => pad + (H - pad * 2) - ((v - minV) / range) * (H - pad * 2);
  const points = vals.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  const last = vals[vals.length - 1];
  const color = last >= 50 ? '#0ECB81' : '#F6465D';
  const y50 = y(50);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 70 }} data-testid="vh-winrate-svg">
      <line x1={pad} x2={W - pad} y1={y50} y2={y50} stroke="#2B3139" strokeDasharray="2,3" strokeWidth="0.5" />
      <text x={pad + 2} y={y50 - 2} fontSize="8" fontFamily="monospace" fill="#5E6673">50%</text>
      <polyline fill="none" stroke={color} strokeWidth="1.4" points={points} />
      <circle cx={pad + (vals.length - 1) * stepX} cy={y(last)} r="2.5" fill={color} />
      <text x={W - pad} y={12} fontSize="10" fontFamily="monospace" fill={color} textAnchor="end">{last.toFixed(1)}%</text>
    </svg>
  );
}

function PnlSparkline({ series }) {
  // series: [{at, cum}] chronological. Renders a smooth cumulative-PnL curve
  // with zero baseline, +green fill if last cum >= 0, red otherwise.
  const W = 320, H = 46, pad = 2;
  const vals = series.map(s => s.cum);
  const minV = Math.min(0, ...vals);
  const maxV = Math.max(0, ...vals);
  const range = Math.max(1e-6, maxV - minV);
  const stepX = (W - pad * 2) / Math.max(1, vals.length - 1);
  const y = v => pad + (H - pad * 2) - ((v - minV) / range) * (H - pad * 2);
  const zeroY = y(0);
  const points = vals.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  const last = vals[vals.length - 1];
  const up = last >= 0;
  const color = up ? '#0ECB81' : '#F6465D';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 50 }} data-testid="pnl-sparkline-svg">
      {/* zero baseline */}
      <line x1={pad} x2={W - pad} y1={zeroY} y2={zeroY} stroke="#2B3139" strokeWidth="0.5" strokeDasharray="2,2" />
      <polygon
        fill={color + '22'}
        points={`${pad},${zeroY} ${points} ${pad + (vals.length - 1) * stepX},${zeroY}`}
      />
      <polyline fill="none" stroke={color} strokeWidth="1.4" points={points} />
      <circle cx={pad + (vals.length - 1) * stepX} cy={y(last)} r="2" fill={color} />
      <text x={W - pad} y={12} fontSize="9" fontFamily="monospace" fill={color} textAnchor="end">
        {up ? '+' : ''}{last.toFixed(2)} USDT
      </text>
    </svg>
  );
}

function WallOfWins() {
  const [wall, setWall] = useState([]);
  const backendUrl = BACKEND;
  useEffect(() => {
    api.get('/futures/wall_of_wins?limit=5').then(r => setWall(r.data.wall || [])).catch(() => {});
  }, []);
  if (wall.length === 0) return null;
  return (
    <div className="mb-8" data-testid="wall-of-wins">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#FCD535]" />
        <h2 className="text-white text-xl font-semibold">Wall of Wins · 7d</h2>
        <span className="text-[10px] font-mono text-[#5E6673] uppercase">Top futures ROE this week</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {wall.map(w => {
          const cardUrl = `${backendUrl}/api/futures/trades/${w.trade_id}/pnl_card.png`;
          const share = () => {
            const text = `🚀 ${w.trader_name} just flipped ${w.symbol} for +${w.roe_pct}% ROE on BITGONODE.`;
            const xUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(cardUrl)}`;
            if (navigator.share) navigator.share({ title: 'BITGONODE PnL', text, url: cardUrl }).catch(() => window.open(xUrl, '_blank'));
            else window.open(xUrl, '_blank', 'noopener,noreferrer');
          };
          return (
            <div key={w.trade_id} className="border border-[#FCD535]/40 bg-gradient-to-br from-[#FCD535]/10 to-transparent p-3" data-testid={`wow-${w.trade_id}`}>
              <div className="flex items-center gap-2 mb-2">
                {w.trader_picture ? <img src={w.trader_picture} className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-[#2B3139] grid place-items-center text-white text-xs">{w.trader_name?.[0]}</div>}
                <Link to={`/trader/${w.user_id}`} className="text-white text-sm truncate hover:text-[#FCD535]">{w.trader_name}</Link>
              </div>
              <div className="text-[10px] font-mono text-[#848E9C] uppercase">{w.symbol} · {w.side?.toUpperCase()} · {w.leverage}×</div>
              <div className="text-2xl font-mono font-bold text-[#0ECB81] mt-1">+{w.roe_pct}%</div>
              <div className="text-[11px] font-mono text-[#B7BDC6]">+${w.realized_pnl_usdt} USDT</div>
              <Button onClick={share} size="sm" className="w-full h-7 text-[11px] mt-2 bg-[#2B3139] hover:bg-[#3B4149] text-white" data-testid={`wow-share-${w.trade_id}`}>
                <Share2 className="w-3 h-3 mr-1" /> Share
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Traders() {
  const { user } = useAuth();
  const [traders, setTraders] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [following, setFollowing] = useState([]);
  const [futuresLb, setFuturesLb] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [futuresSort, setFuturesSort] = useState('roe'); // 'roe' | 'pnl'
  const [profileLeader, setProfileLeader] = useState(null);

  const load = async () => {
    const [t, lb, f, fx] = await Promise.all([
      api.get('/traders/top'),
      api.get('/traders/leaderboard'),
      api.get('/traders/following/mine').catch(() => ({ data: { following: [] } })),
      api.get(`/futures/leaderboard?limit=50&sort=${futuresSort}`).catch(() => ({ data: { leaderboard: [] } })),
    ]);
    setTraders(t.data.traders);
    setLeaderboard(lb.data.traders);
    setFollowing(f.data.following || []);
    setFuturesLb(fx.data.leaderboard || []);
  };
  useEffect(() => { load(); }, [futuresSort]);

  // Live WebSocket push for futures leaderboard — with exponential-backoff reconnect
  // so a dropped connection recovers automatically without a full page refresh.
  const [wsStatus, setWsStatus] = useState('connecting'); // connecting | live | reconnecting
  useEffect(() => {
    const backendUrl = BACKEND;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws/futures/leaderboard';
    let ws;
    let attempts = 0;
    let timer = null;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => { attempts = 0; setWsStatus('live'); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === 'leaderboard' && Array.isArray(msg.leaderboard)) {
            setFuturesLb(msg.leaderboard);
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { /* handled by onclose */ };
      ws.onclose = () => {
        if (cancelled) return;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      setWsStatus('reconnecting');
      // 1s, 2s, 4s, 8s, 16s → cap at 30s
      const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempts, 5)));
      attempts += 1;
      timer = setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, []);

  const unfollow = async (leader_id) => {
    try {
      await api.delete(`/traders/follow/${leader_id}`);
      toast.success('Unfollowed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const followingMap = Object.fromEntries(following.map(f => [f.leader_id, f]));

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Copy Traders</h1>
        <p className="text-[#848E9C] text-sm mt-1">Follow top traders and auto-mirror every trade into your wallet.</p>
      </div>

      {following.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Copy className="w-4 h-4 text-[#FCD535]" />
            <h2 className="text-white text-xl font-semibold">Currently Copying ({following.length})</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {following.map(f => (
              <div key={f.leader_id} className="bg-[#181A20] border border-[#FCD535]/30 p-4 flex items-center gap-3">
                {f.picture ? <img src={f.picture} className="w-10 h-10 rounded-full" /> : (
                  <div className="w-10 h-10 rounded-full bg-[#2B3139] grid place-items-center text-white font-semibold">{f.name?.[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{f.name}</div>
                  <div className="text-xs text-[#FCD535] font-mono">{f.alloc_pct}% allocation</div>
                </div>
                <Button size="sm" variant="ghost" onClick={()=>unfollow(f.leader_id)} className="text-[#F6465D] hover:bg-[#F6465D]/10 hover:text-[#F6465D]" data-testid={`unfollow-${f.leader_id}`}>
                  <UserMinus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <WallOfWins />

      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-[#FCD535]" />
        <h2 className="text-white text-xl font-semibold">Top Traders</h2>
      </div>
      <Tabs defaultValue="roi" className="mb-6">
        <TabsList className="bg-[#181A20] border border-[#2B3139] rounded-sm">
          <TabsTrigger value="roi" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-roi">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Spot · ROI
          </TabsTrigger>
          <TabsTrigger value="volume" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-volume">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Spot · Volume
          </TabsTrigger>
          <TabsTrigger value="futures" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-futures">
            <Zap className="w-3.5 h-3.5 mr-1.5" /> Futures · 7d ROE
          </TabsTrigger>
        </TabsList>
        <TabsContent value="roi">
          <div className="border border-[#2B3139] bg-[#181A20] mt-3" data-testid="roi-leaderboard">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-1">#</div>
              <div className="col-span-4">Trader</div>
              <div className="col-span-2 text-right">Portfolio</div>
              <div className="col-span-2 text-right">ROI %</div>
              <div className="col-span-1 text-right">Copiers</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {leaderboard.length === 0 ? (
              <div className="p-8 text-center text-[#848E9C] text-sm">No traders with completed trades yet.</div>
            ) : leaderboard.map((t, i) => {
              const roiUp = t.roi_pct >= 0;
              return (
                <div key={t.user_id} className={`grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm ${i < 3 ? 'bg-gradient-to-r from-[#FCD535]/5 to-transparent' : ''}`}>
                  <div className="col-span-1 font-mono text-lg">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span className="text-[#848E9C] text-sm">#{i + 1}</span>}
                  </div>
                  <Link to={`/trader/${t.user_id}`} className="col-span-4 flex items-center gap-2 hover:text-[#FCD535]" data-testid={`profile-link-${t.user_id}`}>
                    {t.picture ? <img src={t.picture} className="w-8 h-8 rounded-full" /> : (
                      <div className="w-8 h-8 rounded-full bg-[#2B3139] grid place-items-center text-white font-semibold text-xs">{t.name?.[0]?.toUpperCase() || '?'}</div>
                    )}
                    <div className="text-white truncate">{t.name}</div>
                  </Link>
                  <div className="col-span-2 text-right font-mono text-white">${t.portfolio_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className={`col-span-2 text-right font-mono font-semibold ${roiUp ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{roiUp && '+'}{t.roi_pct}%</div>
                  <div className="col-span-1 text-right font-mono text-[#FCD535]">{t.followers}</div>
                  <div className="col-span-2 flex justify-end">
                    {user && user.user_id !== t.user_id && (
                      <FollowDialog leader={t} onFollowed={load} existing={followingMap[t.user_id]} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="volume">
          <div className="border border-[#2B3139] bg-[#181A20] mt-3" data-testid="traders-table">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Trader</div>
          <div className="col-span-2 text-right">Trades</div>
          <div className="col-span-2 text-right">Volume</div>
          <div className="col-span-1 text-right">Copiers</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {traders.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No traders yet. Be the first to place a trade!</div>
        ) : traders.map((t, i) => (
          <div key={t.user_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
            <div className="col-span-1 font-mono text-[#848E9C]">{i + 1}</div>
            <div className="col-span-4 flex items-center gap-2">
              {t.picture ? <img src={t.picture} className="w-8 h-8 rounded-full" /> : (
                <div className="w-8 h-8 rounded-full bg-[#2B3139] grid place-items-center text-white font-semibold text-xs">{t.name?.[0]?.toUpperCase() || '?'}</div>
              )}
              <Link to={`/trader/${t.user_id}`} className="text-white hover:text-[#FCD535]" data-testid={`vol-profile-link-${t.user_id}`}>{t.name}</Link>
            </div>
            <div className="col-span-2 text-right font-mono text-white">{t.trades_30d}</div>
            <div className="col-span-2 text-right font-mono text-[#EAECEF]">${t.volume_30d.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="col-span-1 text-right font-mono text-[#FCD535]">{t.followers}</div>
            <div className="col-span-2 flex justify-end">
              {user && user.user_id !== t.user_id && (
                <FollowDialog leader={t} onFollowed={load} existing={followingMap[t.user_id]} />
              )}
            </div>
          </div>
        ))}
      </div>
        </TabsContent>
        <TabsContent value="futures">
          <div className="border border-[#2B3139] bg-[#181A20] mt-3" data-testid="futures-leaderboard">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2B3139] text-[10px] font-mono uppercase flex-wrap">
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${wsStatus === 'live' ? 'bg-[#0ECB81] animate-pulse' : wsStatus === 'reconnecting' ? 'bg-[#F0B90B] animate-pulse' : 'bg-[#5E6673]'}`}
                data-testid="futures-ws-status-dot"
              />
              <span className={wsStatus === 'live' ? 'text-[#0ECB81]' : wsStatus === 'reconnecting' ? 'text-[#F0B90B]' : 'text-[#848E9C]'} data-testid="futures-ws-status">
                {wsStatus === 'live' ? 'Live · pushed every 10s' : wsStatus === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
              </span>
              {/* Filters */}
              <div className="ml-4 flex items-center gap-2">
                <label className="flex items-center gap-1 cursor-pointer" data-testid="futures-verified-only-label">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={e => setVerifiedOnly(e.target.checked)}
                    className="accent-[#1DA1F2]"
                    data-testid="futures-verified-only"
                  />
                  <BadgeCheck className="w-3 h-3 text-[#1DA1F2] fill-[#1DA1F2]/20" />
                  <span className={verifiedOnly ? 'text-[#1DA1F2]' : 'text-[#848E9C]'}>Verified only</span>
                </label>
                <select
                  value={futuresSort}
                  onChange={e => setFuturesSort(e.target.value)}
                  className="bg-[#0B0E11] border border-[#2B3139] text-[#B7BDC6] px-1.5 py-0.5 uppercase font-mono"
                  data-testid="futures-sort"
                >
                  <option value="roe">Sort · ROE</option>
                  <option value="pnl">Sort · PnL</option>
                </select>
              </div>
              <span className="ml-auto text-[#5E6673] normal-case">
                <BadgeCheck className="w-3 h-3 text-[#1DA1F2] fill-[#1DA1F2]/20 inline mr-1" /> = 90+ days profitable
              </span>
            </div>
            {!user && futuresLb.length > 0 && (
              <div className="px-4 py-2 border-b border-[#2B3139] bg-[#FCD535]/5 flex items-center gap-2" data-testid="futures-lb-anon-cta">
                <Sparkles className="w-3.5 h-3.5 text-[#FCD535] shrink-0" />
                <div className="text-[11px] text-[#B7BDC6] flex-1">Browse freely — <Link to="/login" className="text-[#FCD535] hover:underline">log in</Link> to auto-copy any trader with one click.</div>
              </div>
            )}
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Trader</div>
              <div className="col-span-2 text-right">7d Total PnL</div>
              <div className="col-span-2 text-right">ROE %</div>
              <div className="col-span-2 text-right">Margin at risk</div>
              <div className="col-span-1 text-right">Copiers</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {futuresLb.length === 0 ? (
              <div className="p-8 text-center text-[#848E9C] text-sm">No futures traders yet — open a position to be ranked.</div>
            ) : (verifiedOnly ? futuresLb.filter(t => t.verified_profitable) : futuresLb).length === 0 ? (
              <div className="p-8 text-center text-[#848E9C] text-sm" data-testid="futures-lb-empty-verified">No verified traders yet. Toggle off to see everyone.</div>
            ) : (verifiedOnly ? futuresLb.filter(t => t.verified_profitable) : futuresLb).map((t, i) => {
              const up = t.roe_pct >= 0;
              const pnlUp = t.total_pnl >= 0;
              return (
                <div key={t.user_id} className={`grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm ${i < 3 ? 'bg-gradient-to-r from-[#FCD535]/5 to-transparent' : ''}`} data-testid={`futures-lb-row-${t.user_id}`}>
                  <div className="col-span-1 font-mono text-lg">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span className="text-[#848E9C] text-sm">#{t.rank}</span>}
                  </div>
                  <div className="col-span-3 flex items-center gap-2">
                    <Link to={`/trader/${t.user_id}`} className="flex items-center gap-2 hover:text-[#FCD535] min-w-0">
                      {t.picture ? <img src={t.picture} className="w-8 h-8 rounded-full" /> : (
                        <div className="w-8 h-8 rounded-full bg-[#2B3139] grid place-items-center text-white font-semibold text-xs">{t.name?.[0]?.toUpperCase() || '?'}</div>
                      )}
                      <div className="text-white truncate">{t.name}</div>
                    </Link>
                    {t.verified_profitable && (
                      <button
                        onClick={(e) => { e.preventDefault(); setProfileLeader(t); }}
                        className="p-0.5 rounded-full hover:bg-[#1DA1F2]/20 transition"
                        title="View 90+ days of profitable history"
                        data-testid={`verified-badge-${t.user_id}`}
                        aria-label="Verified — click to see proof of history"
                      >
                        <BadgeCheck className="w-3.5 h-3.5 text-[#1DA1F2] fill-[#1DA1F2]/20 shrink-0" />
                      </button>
                    )}
                  </div>
                  <div className={`col-span-2 text-right font-mono ${pnlUp ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {pnlUp && '+'}{t.total_pnl?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                  <div className={`col-span-2 text-right font-mono font-semibold ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {up && '+'}{t.roe_pct}%
                  </div>
                  <div className="col-span-2 text-right font-mono text-[#B7BDC6]">${t.margin_at_risk?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="col-span-1 text-right font-mono text-[#FCD535]">{t.followers}</div>
                  <div className="col-span-1 flex justify-end">
                    {user && user.user_id !== t.user_id && (
                      <FollowDialog leader={t} onFollowed={load} existing={followingMap[t.user_id]} />
                    )}
                    {!user && (
                      <Link to="/login" className="text-[10px] px-2 py-1 border border-[#2B3139] text-[#848E9C] hover:text-[#FCD535] hover:border-[#FCD535] uppercase font-mono" data-testid={`anon-copy-cta-${t.user_id}`}>
                        Log in to copy
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
      <VerifiedHistoryModal leader={profileLeader} open={!!profileLeader} onOpenChange={(o) => !o && setProfileLeader(null)} />
    </div>
  );
}
